package com.eladfinish.bible.contest.ui.components.dialogs

import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ripple
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar

/**
 * A reusable dialog list that renders rows and reports clicks with (index, item).
 *
 * Notes:
 * - `closeOnClick` is kept for API compatibility, but closing the dialog should be handled
 *   by the caller (e.g., by updating dialog state in your Store/UI layer).
 */
@Composable
fun <T> DialogSelectableList(
    modifier: Modifier = Modifier,
    state: LazyListState = rememberLazyListState(),
    list: List<T>,
    closeOnClick: Boolean = true,
    onClick: (index: Int, item: T) -> Unit = { _, _ -> },
    isEnabled: (index: Int) -> Boolean = { true },
    item: @Composable (index: Int, item: T) -> Unit,
) {
    DialogSelectableList(
        modifier = modifier,
        state = state,
        list = list,
        closeOnClick = closeOnClick,
        onClick = onClick,
        isEnabled = isEnabled,
        key = null,
        contentPadding = PaddingValues(4.dp),
        verticalSpacing = 4.dp,
        cornerRadius = 16.dp,
        item = item,
    )
}

/**
 * Extended overload (optional) with better control (keys, padding, spacing).
 * Keeping it separate lets existing call sites stay unchanged.
 */
@Composable
fun <T> DialogSelectableList(
    modifier: Modifier = Modifier,
    state: LazyListState = rememberLazyListState(),
    list: List<T>,
    closeOnClick: Boolean = true,
    onClick: (index: Int, item: T) -> Unit = { _, _ -> },
    isEnabled: (index: Int) -> Boolean = { true },
    key: ((index: Int, item: T) -> Any)? = null,
    contentPadding: PaddingValues = PaddingValues(4.dp),
    verticalSpacing: Dp = 4.dp,
    cornerRadius: Dp = 16.dp,
    item: @Composable (index: Int, item: T) -> Unit,
) {
    val shape = RoundedCornerShape(cornerRadius)

    LazyColumn(
        modifier = modifier
            .testTag("dialog_list")
            .mouseVerticalScrollbar(state),
        state = state,
        contentPadding = contentPadding,
        verticalArrangement = Arrangement.spacedBy(space = verticalSpacing),
    ) {
        itemsIndexed(
            items = list,
            key = if (key != null) ({ index, value -> key(index, value) }) else null,
        ) { index, value ->
            val interaction = remember { MutableInteractionSource() }

            Card(
                shape = shape,
                colors = CardDefaults.cardColors(
                    containerColor = if (index % 2 == 0) Color.White else Color(0xFFF2EEE9),
                ),
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag("dialog_list_item_$index")
                    .clip(shape)
                    .combinedClickable(
                        interactionSource = interaction,
                        indication = ripple(bounded = true),
                        enabled = isEnabled(index),
                        onClick = {
                            // `closeOnClick` is kept for API compatibility.
                            // The caller should close the dialog by updating state.
                            if (closeOnClick) {
                                // no-op
                            }
                            onClick(index, value)
                        },
                        onLongClick = null,
                    )
            ) {
                item(index, value)
            }
        }
    }
}
